/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Drawer, Typography, useMediaQuery } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import PublishedPdfViewer from './PublishedPdfViewer';
import { useNavigate, useParams } from 'react-router-dom';
import { InfoButton, Loader } from 'components';
import { dayjs } from 'util/shared/date';
import localeData from 'dayjs/plugin/localeData';
import ReportContentWrapper from 'components/shared/ReportContentWrapper';
import ReportsList from '../../components/shared/ReportsList';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import { AdminContentCategory, DashAccess, hasDashAccess } from 'types';
import useReports from '../../hooks/foundersNotes/useReports';
import { ReportsIndexData, ReportsData } from 'types/flowPatrol';
import { FoundersNote } from 'components/foundersNotes/FoundersNote';
import useToast from 'hooks/useToast';
import { userDashAccessState } from 'states';
import { useRecoilValue } from 'recoil';

const DocsPerPage = 15;

const SGReports = () => {
  dayjs.extend(localeData);
  const { id: selectedDocKey } = useParams();
  const toast = useToast();

  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const dashAccess = useRecoilValue(userDashAccessState);
  const hasInternalAccess = hasDashAccess(dashAccess, DashAccess.INTERNAL);

  const defaultCategories = hasInternalAccess
    ? [AdminContentCategory.FlowPatrol, AdminContentCategory.SundayNotes]
    : [AdminContentCategory.FlowPatrol];

  const [selectedCategories, setSelectedCategories] =
    useState<AdminContentCategory[]>(defaultCategories);
  const [page, setPage] = useState(1);
  const [showingDrawer, setShowingDrawer] = useState(!isMobile);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const docRef = useRef<HTMLDivElement>(null);

  const { getReportsIndex, getReportsWithId } = useReports();

  // Query for FlowPatrol documents
  const {
    data: flowPatrolResult,
    isLoading: loadingFlowPatrol,
    error: flowPatrolError,
    refetch: refetchFlowPatrol,
  } = useQuery<ReportsIndexData>({
    queryKey: ['flowPatrolIndex', page, selectedMonth, selectedYear],
    queryFn: async () => {
      try {
        const response = await getReportsIndex(
          AdminContentCategory.FlowPatrol,
          page,
          DocsPerPage,
          selectedMonth,
          selectedYear,
        );
        if (response.error != null) {
          throw new Error('Unable to load FlowPatrol documents');
        }
        return response;
      } catch (error) {
        console.error('Error fetching FlowPatrol documents:', error);
        throw new Error('Unable to load FlowPatrol documents');
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1_000, // 5 minutes
    gcTime: 10 * 60 * 1_000, // 10 minutes
    retry: 2,
  });

  // Query for Sunday Notes documents - only if user has internal access
  const {
    data: sundayNotesResult,
    isLoading: loadingSundayNotes,
    error: sundayNotesError,
    refetch: refetchSundayNotes,
  } = useQuery<ReportsIndexData>({
    queryKey: ['sundayNotesIndex', page, selectedMonth, selectedYear],
    queryFn: async () => {
      try {
        const response = await getReportsIndex(
          AdminContentCategory.SundayNotes,
          page,
          DocsPerPage,
          selectedMonth,
          selectedYear,
        );
        if (response.error != null) {
          throw new Error('Unable to load Sunday Notes documents');
        }
        return response;
      } catch (error) {
        console.error('Error fetching Sunday Notes documents:', error);
        throw new Error('Unable to load Sunday Notes documents');
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1_000, // 5 minutes
    gcTime: 10 * 60 * 1_000, // 10 minutes
    retry: 2,
    enabled: hasInternalAccess,
  });

  // Combine and filter documents based on selected categories
  const combinedDocs = useMemo(() => {
    // Wait for all required fetches to complete before combining
    const flowPatrolNeeded = selectedCategories.includes(
      AdminContentCategory.FlowPatrol,
    );
    const sundayNotesNeeded =
      hasInternalAccess &&
      selectedCategories.includes(AdminContentCategory.SundayNotes);

    // If FlowPatrol is needed but still loading, return empty array
    if (flowPatrolNeeded && loadingFlowPatrol) {
      return [];
    }

    // If Sunday Notes is needed but still loading, return empty array
    if (sundayNotesNeeded && loadingSundayNotes) {
      return [];
    }

    const allDocs: ReportsData[] = [];

    if (flowPatrolNeeded && flowPatrolResult?.data) {
      allDocs.push(...flowPatrolResult.data);
    }

    if (sundayNotesNeeded && sundayNotesResult?.data) {
      allDocs.push(...sundayNotesResult.data);
    }

    // Sort by date descending (newest first), with null dates at the end
    return allDocs.sort((a, b) => {
      if (a.date == null && b.date == null) {
        return 0;
      }
      if (a.date == null) {
        return 1;
      }
      if (b.date == null) {
        return -1;
      }
      return dayjs(b.date).valueOf() - dayjs(a.date).valueOf();
    });
  }, [
    selectedCategories,
    flowPatrolResult?.data,
    sundayNotesResult?.data,
    hasInternalAccess,
    loadingFlowPatrol,
    loadingSundayNotes,
  ]);

  const hasNextPage =
    (selectedCategories.includes(AdminContentCategory.FlowPatrol) &&
      flowPatrolResult?.hasNextPage) ||
    (hasInternalAccess &&
      selectedCategories.includes(AdminContentCategory.SundayNotes) &&
      sundayNotesResult?.hasNextPage) ||
    false;

  const getDocByKey = useCallback(
    (docKey: string, docs: ReportsData[]) => {
      return docs.find(
        (doc) =>
          doc.key === `${AdminContentCategory.FlowPatrol}/${docKey}` ||
          (hasInternalAccess &&
            doc.key === `${AdminContentCategory.SundayNotes}/${docKey}`),
      );
    },
    [hasInternalAccess],
  );

  // Query for individual document - only when we have a selected doc key
  const {
    data: selectedDoc,
    isLoading: loadingSelectedDoc,
    error: selectedDocError,
    refetch: refetchSelectedDoc,
  } = useQuery<ReportsData | null>({
    queryKey: ['reportDoc', selectedDocKey],
    queryFn: async () => {
      if (selectedDocKey == null) {
        return null;
      }
      try {
        // Check if we already have this doc with PDF URL in the combined list
        const doc = getDocByKey(selectedDocKey, combinedDocs);

        if (doc == null) {
          return null;
        }

        // Fetch the individual document
        const response = await getReportsWithId(doc.key, doc.category);

        if (response.data == null) {
          throw new Error('Document not found');
        }
        return response.data;
      } catch (error) {
        console.error('Error fetching document:', error);
        throw new Error('Unable to load document');
      }
    },
    refetchOnWindowFocus: true,
    enabled: selectedDocKey != null && combinedDocs.length > 0,
    staleTime: 5 * 60 * 1_000, // 5 minutes
    gcTime: 10 * 60 * 1_000, // 10 minutes
    retry: 2,
  });

  const loadSelectedDoc = useCallback(
    (docKey: string) => {
      if (isMobile) {
        setShowingDrawer(false);
      }
      const cleanKey = docKey
        .replace(`${AdminContentCategory.FlowPatrol}/`, '')
        .replace(`${AdminContentCategory.SundayNotes}/`, '');
      navigate(`/reports/${encodeURIComponent(cleanKey)}`, { replace: true });
    },
    [isMobile, navigate],
  );

  useEffect(() => {
    if (selectedDocKey == null && combinedDocs.length > 0) {
      loadSelectedDoc(combinedDocs[0].key); // initialize with first document
    }
  }, [selectedDocKey, combinedDocs, loadSelectedDoc]);

  const retryFetch = () => {
    if (flowPatrolError || (hasInternalAccess && sundayNotesError)) {
      void refetchFlowPatrol();
      if (hasInternalAccess) {
        void refetchSundayNotes();
      }
    } else if (selectedDocError) {
      void refetchSelectedDoc();
    }
  };

  const refreshSidebar = async () => {
    try {
      setIsRefreshing(true);
      const promises = [refetchFlowPatrol()];
      if (hasInternalAccess) {
        promises.push(refetchSundayNotes());
      }
      await Promise.all(promises);
    } catch (error) {
      console.error('Error refreshing sidebar:', error);
      toast.openToast({
        message: 'Failed to refresh documents. Please try again.',
        type: 'error',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const nextPage = () => {
    if (!hasNextPage) {
      return;
    }
    setPage(page + 1);
  };

  const previousPage = () => {
    if (page <= 1) {
      return;
    }
    setPage(page - 1);
  };

  const toggleShowingDrawer = () => {
    setShowingDrawer(!showingDrawer);
  };

  const handleCategoriesChange = (categories: AdminContentCategory[]) => {
    // Filter out Sunday Notes if user doesn't have internal access
    const filteredCategories = hasInternalAccess
      ? categories
      : categories.filter((cat) => cat !== AdminContentCategory.SundayNotes);

    setSelectedCategories(filteredCategories);
    setPage(1);
    // Reset selected doc if it's not in the new category selection
    if (selectedDocKey && filteredCategories.length > 0) {
      const isSelectedDocInCategories = filteredCategories.some((category) => {
        if (category === AdminContentCategory.FlowPatrol) {
          return selectedDocKey.startsWith(
            `${AdminContentCategory.FlowPatrol}/`,
          );
        }
        if (
          hasInternalAccess &&
          category === AdminContentCategory.SundayNotes
        ) {
          return selectedDocKey.startsWith(
            `${AdminContentCategory.SundayNotes}/`,
          );
        }
        return false;
      });

      if (!isSelectedDocInCategories) {
        loadSelectedDoc(combinedDocs[0].key);
      }
    }
  };

  const downloadPdf = async () => {
    if (!selectedDoc?.pdfUrl || !selectedDocKey) {
      toast.openToast({
        message: 'PDF is not available for download',
        type: 'error',
      });
      return;
    }

    setIsDownloading(true);

    try {
      const pdfUrl = await getPdfUrlForDownload();
      const response = await fetch(pdfUrl);

      if (!response.ok) {
        handleDownloadError(response.status);
        return;
      }

      const blob = await response.blob();
      const filename = generateFilename();

      downloadBlob(blob, filename);

      toast.openToast({
        message: 'PDF downloaded successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      handleDownloadError();
    } finally {
      setIsDownloading(false);
    }
  };

  const getPdfUrlForDownload = async (): Promise<string> => {
    let pdfUrl = selectedDoc?.pdfUrl;
    if (!pdfUrl || !selectedDocKey) {
      throw new Error('No PDF URL available');
    }

    try {
      const response = await fetch(pdfUrl);

      // If the PDF URL has expired, get a fresh one
      if (
        !response.ok &&
        (response.status === 403 || response.status === 404)
      ) {
        toast.openToast({
          message: 'PDF link expired, fetching a fresh download link...',
          type: 'info',
        });

        const category =
          hasInternalAccess && selectedDocKey?.startsWith('sunday-notes/')
            ? AdminContentCategory.SundayNotes
            : AdminContentCategory.FlowPatrol;

        const freshDocResponse = await getReportsWithId(
          selectedDocKey,
          category,
        );

        if (!freshDocResponse.data?.pdfUrl) {
          throw new Error('Unable to get fresh PDF URL');
        }

        pdfUrl = freshDocResponse.data.pdfUrl;
        void refetchSelectedDoc(); // Update the query cache
      }

      return pdfUrl;
    } catch (error) {
      console.error('Error getting PDF URL:', error);
      throw new Error('Unable to access PDF');
    }
  };

  const generateFilename = (): string => {
    try {
      const baseName = selectedDoc?.title || selectedDoc?.key;
      if (!baseName) {
        return 'document.pdf';
      }
      return `${baseName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
    } catch (error) {
      console.error('Error generating filename:', error);
      return 'document.pdf';
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw new Error('Unable to download file');
    }
  };

  const handleDownloadError = (status?: number | null) => {
    let message: string;

    if (status === 403) {
      message =
        'Access denied. You do not have permission to download this PDF.';
    } else if (status === 404) {
      message =
        'The PDF file is no longer available. Please refresh the page and try again.';
    } else {
      message = 'Failed to download PDF. Please try again later.';
    }

    toast.openToast({
      message,
      type: 'error',
    });
  };

  const availableCategories = [
    { value: AdminContentCategory.FlowPatrol, label: 'FlowPatrol' },
    ...(hasInternalAccess
      ? [{ value: AdminContentCategory.SundayNotes, label: 'Sunday Notes' }]
      : []),
  ];

  const docsListComponent = showingDrawer && (
    <ReportsList
      items={combinedDocs}
      selectedItemKey={selectedDoc?.key ?? null}
      categories={availableCategories}
      selectedCategories={selectedCategories}
      onCategoriesChange={handleCategoriesChange}
      IconComponent={DescriptionIcon}
      onItemSelect={(key: string) => {
        if (isMobile) {
          setShowingDrawer(false);
        }
        loadSelectedDoc(key);
      }}
      onRefresh={refreshSidebar}
      onClose={toggleShowingDrawer}
      selectedMonth={selectedMonth}
      selectedYear={selectedYear}
      onMonthChange={(month: number) => {
        setSelectedMonth(month);
        setPage(1);
      }}
      onYearChange={(year: number) => {
        const newYear = year;
        if (newYear === dayjs().year() && selectedMonth > dayjs().month()) {
          setSelectedMonth(dayjs().month());
        }
        setSelectedYear(newYear);
        setPage(1);
      }}
      loading={loadingFlowPatrol || loadingSundayNotes || isRefreshing}
      currentPage={page}
      hasNextPage={hasNextPage}
      onNextPage={nextPage}
      onPreviousPage={previousPage}
      showPagination={true}
      paginationCondition={true}
      firstNewItemIndex={0}
    />
  );

  const sidebar = isMobile ? (
    <Drawer
      variant="temporary"
      anchor="right"
      open={showingDrawer}
      onClose={() => setShowingDrawer(false)}
    >
      {docsListComponent}
    </Drawer>
  ) : (
    docsListComponent
  );

  const isLoading =
    loadingFlowPatrol || loadingSundayNotes || loadingSelectedDoc;
  const hasError = flowPatrolError || sundayNotesError || selectedDocError;
  const hasNoDocs = combinedDocs.length === 0;

  const loadingOrErrored = isLoading ? (
    <Loader isLoading={isLoading} />
  ) : hasError ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: 4,
      }}
    >
      <Typography variant="h6" color="text.secondary">
        Unable to load documents
      </Typography>
      <Button variant="outlined" onClick={retryFetch}>
        Try Again
      </Button>
    </Box>
  ) : hasNoDocs ? (
    <Typography variant="h6" color="text.secondary">
      There are no documents available for this month/year.
    </Typography>
  ) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        justifyContent: 'space-between',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <Box
        sx={{
          paddingRight: 5,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <Button
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontSize: '12px',
            height: '30px',
            whiteSpace: 'nowrap',
          }}
          onClick={toggleShowingDrawer}
        >
          {showingDrawer ? 'Hide all documents' : 'View all documents'}
        </Button>
        {selectedDoc?.pdfUrl && (
          <Button
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              height: '30px',
              whiteSpace: 'nowrap',
              display: isMobile ? 'flex' : 'none',
            }}
            onClick={() => void downloadPdf()}
            startIcon={<DownloadIcon />}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download PDF'}
          </Button>
        )}
        <Box
          sx={{
            justifySelf: 'flex-end',
            gap: '4px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <InfoButton key="flow-patrol-info" articleKey="flow-patrol-info" />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: {
            xs: '100%',
            lg: 'calc(100vh - 170px)',
          },
          justifyContent: 'space-between',
          flexDirection: 'row',
        }}
      >
        <ReportContentWrapper
          ref={docRef}
          sx={{ position: 'relative', paddingX: 4, paddingY: 0 }}
        >
          {selectedDoc?.pdfUrl && (
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                display: isMobile ? 'none' : 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  height: '30px',
                  whiteSpace: 'nowrap',
                }}
                onClick={() => void downloadPdf()}
                startIcon={<DownloadIcon />}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Download PDF'}
              </Button>
            </Box>
          )}
          {loadingOrErrored ??
            (selectedDoc?.pdfUrl != null ? (
              <PublishedPdfViewer
                pdfUrl={selectedDoc.pdfUrl}
                mode="all-pages"
              />
            ) : selectedDoc?.html != null ? (
              <Box style={{ overflow: 'auto' }}>
                <FoundersNote
                  html={selectedDoc && selectedDoc.html ? selectedDoc.html : ''}
                  title={selectedDoc?.title}
                  id={selectedDoc?.key}
                  date={dayjs(selectedDoc?.date)}
                  fromWordpress={false}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Select a document to view
                </Typography>
              </Box>
            ))}
        </ReportContentWrapper>
        {sidebar}
      </Box>
    </Box>
  );
};

export default SGReports;
